import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateRangeFilterDefinition } from '@asset-metrix/portal-gateway-client-js';
import { MonthRangePicker, QuarterRangePicker } from '@asset-metrix/portal-ui';
import { useShallow } from 'zustand/react/shallow';

import { useStore } from '../../store';

import { getMonthRangePresets } from './periodRangeFilter';

export type PeriodRangePickerFilterProps = {
  entityId: string;
  filterData?: DateRangeFilterDefinition;
  filterKey: string;
  isFetching?: boolean;
  disabled?: boolean;
  label?: ReactNode | ReactNode[];
  display: 'month-range-picker' | 'quarter-range-picker';
};

export function PeriodRangePickerFilter({
  disabled,
  display,
  entityId,
  filterData,
  filterKey,
  isFetching,
  label,
}: PeriodRangePickerFilterProps) {
  const { t } = useTranslation();
  const type = display === 'month-range-picker' ? 'month' : 'quarter';
  const [setFilterSelectionValue, resetFilterSelections] = useStore(
    useShallow((state) => [
      state.setFilterSelectionValue,
      state.resetFilterSelections,
    ])
  );
  const [value, setValue] = useState(() => filterData?.selection ?? {});
  const presets = useMemo(
    () => (type === 'month' ? getMonthRangePresets(t) : undefined),
    [t, type]
  );
  const hasFilterData = filterData != null;
  const serverStart = filterData?.selection?.start;
  const serverEnd = filterData?.selection?.end;

  useEffect(() => {
    if (!hasFilterData) {
      return;
    }
    setValue({
      ...(serverStart != null ? { start: serverStart } : {}),
      ...(serverEnd != null ? { end: serverEnd } : {}),
    });
  }, [hasFilterData, serverEnd, serverStart]);

  const pickerProps = {
    disabled,
    label,
    loading: isFetching,
    onChange: setValue,
    onCommit: (range: typeof value) => {
      if (range.start == null && range.end == null) {
        resetFilterSelections(entityId, filterKey);
      } else {
        setFilterSelectionValue(entityId, filterKey, {
          start: range.start,
          end: range.end,
        });
      }
    },
    placeholderFrom: t('app.pageFilter.dateRangeFilter.placeholderFrom'),
    placeholderTo: t('app.pageFilter.dateRangeFilter.placeholderTo'),
    presets,
    value,
  };

  return type === 'month' ? (
    <MonthRangePicker {...pickerProps} />
  ) : (
    <QuarterRangePicker {...pickerProps} />
  );
}
